import AsyncSelect from "react-select/async";
import useEntitySelect from "../../../../../../hooks/useEntitySelect";
import { carSchema } from "../../../../../car/domain/schemas/carSchema";
import useCrudFilters from "../../../../../../hooks/useCrudFilters";
import LicensePlate from "license-plate";
import { CrudDataModel } from "../../../../../../hooks/useCrud";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import licencePlateSelectBg from "./assets/licencePlateSelectBg.svg";
import { EntitySelectFieldBaseModel } from "../entitySelectField/entitySelectFieldModel";

export const LicencePlateSelectField = ({
  isInvalid,
  onChange,
  value,
  isMulti = true,
}: EntitySelectFieldBaseModel) => {
  const FILTER_KEY = "licence";
  const FILTER_TYPE = "contains";

  const { filters, updateFilter, getFilter, removeFilter } = useCrudFilters(
    {
      searchBy: "SearchByRootCar",
      fields: {},
    },
    "licencePlate",
  );

  const searchValue = getFilter(FILTER_KEY, FILTER_TYPE);

  const { t } = useTranslation("common");

  const { ref, options, loading, reload } = useEntitySelect({
    labelPrefix: FILTER_KEY,
    prefix: "cars",
    entity: "Car",
    schema: carSchema,
    limit: 100,
    filters: filters,
    filterReloadOnSearch: true,
    value: value,
    optionsMap: (option) => {
      if (typeof option.label !== "string") return option;
      const plate = new LicensePlate(option.label);
      if (!plate.valid()) return option;
      return {
        value: option.value,
        label: plate.pretty(),
      };
    },
  });

  const loadOptionsAsync = (inputValue: string) =>
    new Promise<CrudDataModel[]>((resolve) => {
      updateFilter(
        FILTER_KEY,
        inputValue.toUpperCase().replace(/-/g, ""),
        FILTER_TYPE,
        (filters) => {
          void reload(filters).then((options) => {
            resolve(options);
          });
        },
      );
    });

  useEffect(() => {
    if (value === undefined) return ref.current?.setValue([], "select-option");
  }, [value]);

  useEffect(() => {
    if (value === undefined || !Array.isArray(value) || !onChange) return;

    const valueOptions = value.map((option) => {
      const plate = new LicensePlate(option.toString());
      return {
        label: plate.pretty(),
        value: option,
      };
    });
    onChange(valueOptions);

    ref.current?.setValue(valueOptions, "select-option");
  }, []);

  return (
    <div className={`entity-select-wrapper${isInvalid ? " is-invalid" : ""}`}>
      <AsyncSelect
        placeholder={t("Select...")}
        ref={ref}
        className={"entity-select"}
        classNamePrefix={"entity-select"}
        loadOptions={loadOptionsAsync}
        defaultOptions={options}
        cacheOptions
        isLoading={loading}
        onChange={(options: unknown) => {
          removeFilter(FILTER_KEY);
          if (onChange) {
            onChange(options);
          }
        }}
        isClearable={true}
        isMulti={isMulti}
        noOptionsMessage={() => {
          if (searchValue !== undefined)
            return t("No results found for '{{searchValue}}'", {
              searchValue,
            });
          return t("Search for a license plate");
        }}
        styles={{
          singleValue: (base) => ({
            ...base,
            background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
            width: "112px",
            height: "23px",
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "flex-end",
            alignItems: "center",
            fontSize: "14px",
            fontWeight: 500,
            color: "#000",
            paddingLeft: "20px",
            letterSpacing: "2px",
          }),
          multiValue: (base) => ({
            ...base,
            background: `url(${licencePlateSelectBg}) no-repeat center center / cover`,
            width: "112px",
            height: "23px",
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "flex-end",
            alignItems: "center",
          }),
          multiValueRemove: () => ({
            color: "#fff",
            height: "23px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "15px",
          }),
          multiValueLabel: () => ({
            fontSize: "14px",
            fontWeight: 500,
            color: "#000",
            height: "auto",
            width: "100%",
            paddingLeft: "5px",
            letterSpacing: "2px",
          }),
        }}
      />
    </div>
  );
};
